import type { User } from '../types'

const KEYS = {
  accessToken: 'accessToken',
  refreshToken: 'refreshToken',
  userId: 'userId',
  username: 'username',
  roles: 'userRoles'
} as const

export const AUTH_LOGOUT_EVENT = 'auth:logout'

const safeAtob = (input: string): string => {
  // Vite/browser environment: `atob` is expected to exist.
  return atob(input)
}

const base64UrlToBase64 = (b64url: string): string => {
  const normalized = b64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = normalized.length % 4
  if (pad === 0) return normalized
  return normalized + '='.repeat(4 - pad)
}

export const decodeTokenPayload = (token: string): Record<string, any> | null => {
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const json = safeAtob(base64UrlToBase64(parts[1]))
    return JSON.parse(json) as Record<string, any>
  } catch {
    return null
  }
}

export const getTokenExpirationMs = (token: string): number | null => {
  const payload = decodeTokenPayload(token)
  const exp = payload?.exp
  if (typeof exp !== 'number') return null
  return exp * 1000
}

export const getTimeUntilExpirationMs = (token: string): number | null => {
  const expMs = getTokenExpirationMs(token)
  if (expMs === null) return null
  return expMs - Date.now()
}

export const isTokenExpired = (token: string): boolean => {
  const time = getTimeUntilExpirationMs(token)
  if (time === null) return false
  return time <= 0
}

export const dispatchAuthLogoutEvent = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT))
}

export const saveAuthData = (data: {
  accessToken: string
  refreshToken: string
  userId: string
  username: string
  roles: string[]
}) => {
  localStorage.setItem(KEYS.accessToken, data.accessToken)
  localStorage.setItem(KEYS.refreshToken, data.refreshToken)
  localStorage.setItem(KEYS.userId, data.userId)
  localStorage.setItem(KEYS.username, data.username)
  localStorage.setItem(KEYS.roles, JSON.stringify(data.roles))
}

export const clearAuthData = () => {
  Object.values(KEYS).forEach((k) => localStorage.removeItem(k))
}

export const logoutEverywhere = () => {
  clearAuthData()
  dispatchAuthLogoutEvent()
}

export const loadUserFromStorage = (): User | null => {
  const accessToken = localStorage.getItem(KEYS.accessToken)
  const userId = localStorage.getItem(KEYS.userId)
  const username = localStorage.getItem(KEYS.username)
  const rolesRaw = localStorage.getItem(KEYS.roles)

  if (!accessToken || !userId || !username || !rolesRaw) return null
  if (isTokenExpired(accessToken)) {
    clearAuthData()
    return null
  }

  try {
    const roles = JSON.parse(rolesRaw) as string[]
    return { userId, username, roles }
  } catch {
    return null
  }
}
